import { useContext, useState } from "react";

import { SessionContext } from "@context/session.context";
import { ConfigurationKeys } from "@services/configuration";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";
import { isNullishFromString, stringToBoolean } from "@utils/utils";

const configurationDescriptionOverride = {
  [ConfigurationKeys.OFFLINE_MODE_DURATION]:
    "Set the duration for temporary offline mode, up to 7 days.",
};

const parseConfigurationValue = (key, value) => {
  switch (key) {
    case ConfigurationKeys.OFFLINE_MODE_ENABLED:
    case ConfigurationKeys.SKIP_SIM_EXIT_POPUP:
    case ConfigurationKeys.COURSE_BUILDER_ENABLED:
    case ConfigurationKeys.AI_CURRICULUM_ENABLED:
    case ConfigurationKeys.IMPACT_REPORT_PROMPT_CONFIG_ENABLED: {
      return stringToBoolean(value);
    }
    case ConfigurationKeys.OFFLINE_MODE_DURATION: {
      return Number(value);
    }
    default: {
      return isNullishFromString(value) || value === "" ? undefined : value;
    }
  }
};

export const useConfigurations = (appId, organizationId, options = {}) => {
  const { configurationService } = services;
  const { role } = useContext(SessionContext);

  const [data, setData] = useState();

  const {
    loading: loadingOrgConfigurations,
    sendRequest: getOrgConfigurations,
  } = useApiRequest(
    () =>
      configurationService.getOrganizationConfiguration(appId, organizationId),
    false,
  );

  const {
    loading: loadingAllConfigurations,
    sendRequest: getAllConfigurations,
  } = useApiRequest(
    () => configurationService.getConfigurations(appId, role),
    false,
  );

  const getConfigurations = async () => {
    const [allConfigurations, orgConfigurations] = await Promise.all([
      getAllConfigurations(),
      getOrgConfigurations(),
    ]);

    const mergedConfigurations = allConfigurations?.config_settings
      // Merge the configurations with the organization configurations
      .map((config) => {
        const orgConfig = orgConfigurations.config_settings.find(
          (value) => value.id === config.id,
        );

        return {
          ...config,
          value: orgConfig?.value,
        };
      })
      // Normalize the configurations by the name
      .reduce((acc, curr) => {
        const key = curr.name;
        const overrideDescription = configurationDescriptionOverride[key];
        acc[key] = {
          ...curr,
          value: parseConfigurationValue(key, curr.value),
          description: overrideDescription ?? curr.description,
        };
        return acc;
      }, {});

    mergedConfigurations["whitelistedUrls"] =
      orgConfigurations.whitelisted_urls;

    options.onSuccess?.(mergedConfigurations);
    setData(mergedConfigurations);
  };

  return {
    loading: loadingOrgConfigurations || loadingAllConfigurations,
    data,
    getConfigurations,
  };
};
